/**
 * DeleteConversationSheet — Plan 05-08b.
 *
 * Two-step confirmation BottomSheet for deleting a conversation.
 * Replicates the Phase 4 SweepConfirmSheet destructive UX pattern:
 * - Default focus on Avbryt (the safe choice)
 * - Slett button in the design system's destructive fill (bg-bad)
 * - Calls useAssistantMutations.remove on confirm
 *
 * Per UI-SPEC §"Destructive actions": default focus on cancel,
 * destructive fill for the destructive action. That fill is `bg-bad` —
 * the token `Button variant="danger"` uses at its nine destructive call
 * sites — not `bg-primary`, which Button.tsx reserves as the primary
 * accent CTA. It was `bg-error` until issue #78: no `--color-error` token
 * exists, so the class resolved to nothing and the label painted
 * text-sheet on the panel's bg-bg at ~1.1:1, i.e. invisible.
 *
 * A failing delete reports inline via <SheetError>, never via a toast:
 * this sheet is at z-50 and ToastViewport at z-40 (Pattern 7 in
 * docs/ARCHITECTURE.md), so a toast would be painted behind the scrim.
 * The mutation is awaited inside a try/catch for the same reason the
 * surface exists — an escaping rejection is a failure nobody is told
 * about (issue #78). Only success closes the sheet.
 *
 * T-05-08b-DELETE-CONFIRM: two-step confirmation prevents misclick.
 */
import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BottomSheet } from '../../components/primitives/BottomSheet';
import { SheetError } from '../../components/primitives/SheetError';
import { useAssistantMutations } from './useAssistantMutations';

interface DeleteConversationSheetProps {
  id: string;
  open: boolean;
  onClose: () => void;
}

export function DeleteConversationSheet({ id, open, onClose }: DeleteConversationSheetProps) {
  const { t } = useTranslation();
  const { remove } = useAssistantMutations();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [error, setError] = useState<string | null>(null);

  // Default focus on Avbryt per Phase 1 destructive UX rule.
  // BottomSheet auto-focuses the first focusable child; Avbryt is rendered
  // first so it gets natural focus. The useEffect is a safety net.
  useEffect(() => {
    if (open) {
      cancelRef.current?.focus();
    }
  }, [open]);

  async function onConfirm(): Promise<void> {
    // Clear any stale message first — a second attempt must not sit under
    // the copy from the first one.
    setError(null);
    try {
      await remove.mutateAsync(id);
    } catch {
      setError(t('errors.generic.body'));
      return;
    }
    onClose();
  }

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t('assistant.conversations.delete_confirm.title')}
    >
      <div data-testid="assistant-delete-conversation-sheet" className="flex flex-col gap-3">
        <p className="text-base text-ink-2 mb-2">
          {t('assistant.conversations.delete_confirm.body')}
        </p>
        <SheetError message={error} data-testid="assistant-delete-error" />
        <button
          ref={cancelRef}
          type="button"
          data-testid="assistant-delete-cancel"
          onClick={onClose}
          className="h-11 min-h-11 rounded-md bg-sheet text-ink border border-card-line font-medium text-sm"
        >
          {t('assistant.conversations.delete_confirm.cancel')}
        </button>
        <button
          type="button"
          data-testid="assistant-delete-confirm"
          className="h-11 min-h-11 rounded-md bg-bad text-sheet font-medium text-sm"
          onClick={() => { void onConfirm(); }}
        >
          {t('assistant.conversations.delete_confirm.confirm')}
        </button>
      </div>
    </BottomSheet>
  );
}
